import IOSNotification, { isIOSEnv } from './IOSNotification';
import AndroidNotification, { isAndroidEnv } from './AndroidNotification';
import WebPushNotification, { isLikelyTWA } from './WebPushNotification';
import NotificationBase, {
  onClickNotificationHandler,
  onMessageHandler,
  TokenType,
} from './NotificationBase';
import EmptyNotification from './EmptyNotification';
import { isAndroid } from '../../constants/devices';

declare global {
  interface Window {
    webkit: any;
    iOSPushCapability: boolean;
  }
}

const ssr = typeof window === 'undefined';

async function selectImpl(): Promise<NotificationBase> {
  if (ssr) return new EmptyNotification();
  try {
    // iOS WKWebView 는 표준 `Notification` API 를 노출하지 않을 수 있으므로
    // 반드시 네이티브 브릿지 존재 여부(`isIOSEnv()`)를 먼저 확인해야 한다.
    // 이 체크가 `Notification` 존재 체크 뒤로 가면 iOS 가 EmptyNotification
    // 로 빠져 토큰 등록/알림이 전부 no-op 이 된다.
    if (isIOSEnv()) {
      return new IOSNotification();
    }
    if (!('Notification' in window)) {
      return new EmptyNotification();
    }
    // 네이티브 Android 핸드셰이크는 이 페이지가 우리 TWA 안에서 돌고
    // 있다는 증거(android-app referrer / source=pwa 쿼리 / 세션 플래그)가
    // 있을 때만 기다린다. 일반 Chrome-Android 브라우저 세션은 타임아웃을
    // 전부 건너뛰고 곧바로 WebPushNotification으로 동작한다.
    if (isAndroid() && isLikelyTWA() && (await isAndroidEnv())) {
      return new AndroidNotification();
    }
    return new WebPushNotification();
  } catch {
    return new EmptyNotification();
  }
}

// 실제 구현체가 선택되기 전까지 메서드 호출을 지연시키는 프록시
// (Android 핸드셰이크 타임아웃 범위 내로 bounded). 기존 모듈 API를 그대로
// 동기 인터페이스로 유지해서 소비자 쪽에 await를 요구하지 않는다.
class NotificationProxy extends NotificationBase {
  private implPromise: Promise<NotificationBase>;
  private impl: NotificationBase | null = null;

  constructor() {
    super();
    this.implPromise = selectImpl();
    this.implPromise.then((impl) => {
      this.impl = impl;
    });
  }

  private getImpl(): Promise<NotificationBase> {
    return this.impl ? Promise.resolve(this.impl) : this.implPromise;
  }

  async requestPermission() {
    return (await this.getImpl()).requestPermission();
  }

  async getPermissionState() {
    return (await this.getImpl()).getPermissionState();
  }

  async requestToken(): Promise<TokenType | null> {
    return (await this.getImpl()).requestToken();
  }

  async onMessage(handler: onMessageHandler) {
    return (await this.getImpl()).onMessage(handler);
  }

  setBadgeCount(count: number) {
    void this.getImpl().then((impl) => impl.setBadgeCount(count));
  }

  onNotificationClick(handler: onClickNotificationHandler) {
    let innerUnsubscribe: (() => void) | null = null;
    let cancelled = false;
    void this.getImpl().then((impl) => {
      if (cancelled) return;
      innerUnsubscribe = impl.onNotificationClick(handler);
    });
    return () => {
      cancelled = true;
      innerUnsubscribe?.();
    };
  }

  async clearNotificationsByChannel(channelId: string): Promise<void> {
    return (await this.getImpl()).clearNotificationsByChannel(channelId);
  }

  async clearAllNotifications(): Promise<void> {
    return (await this.getImpl()).clearAllNotifications();
  }

  supportsNotifications(): boolean {
    return this.impl?.supportsNotifications() ?? true;
  }

  setActiveChannel(channelId: string | null): void {
    void this.getImpl().then((impl) => impl.setActiveChannel(channelId));
  }
}

let notification: NotificationBase;

try {
  notification = new NotificationProxy();
} catch {
  notification = new EmptyNotification();
}

export default notification;
